'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/numbers-extra'

const UnitCircle = dynamic(() => import('../_components/widgets/FoundationWidgets').then(m => m.UnitCircle), { ssr: false })
const WaveExplorer = dynamic(() => import('../_components/widgets/FoundationWidgets').then(m => m.WaveExplorer), { ssr: false })
const AngleBetween = dynamic(() => import('../_components/widgets/FoundationWidgets').then(m => m.AngleBetween), { ssr: false })

export default function Trigonometry() {
  return (
    <Lesson>
      <Step>
        <H>Turning, measured</H>
        <P>
          Trigonometry sounds scary, but it’s about one simple thing: <strong>turning</strong>. Take a circle of radius 1 (the{' '}
          <strong>unit circle</strong>) and walk around it. At any angle, ask two questions: how far right am I, and how far up?
        </P>
        <P>
          Those two answers have names: <Hl c="blue">cosine</Hl> (how far across) and <Hl c="amber">sine</Hl> (how far up).
          That’s all <M>{'\\cos'}</M> and <M>{'\\sin'}</M> are.
        </P>
        <Figure n="2.12" wide caption="Drag the point around the circle (or press Spin). Cosine is its shadow on the horizontal axis, sine its shadow on the vertical one. Unroll the sine as the angle grows and you get a wave.">
          <UnitCircle />
        </Figure>
      </Step>

      <Step gate={['q-cos', 'nq-rad']}>
        <H>Degrees and radians</H>
        <P>
          You probably know angles in <strong>degrees</strong>: 360° is a full turn. Maths and code use{' '}
          <strong>radians</strong> instead: the distance travelled around the unit circle. One full turn is the circle’s
          circumference, <M>{'2\\pi \\approx 6.283'}</M>.
        </P>
        <MB>{'\\text{radians} = \\text{degrees} \\times \\frac{\\pi}{180}'}</MB>
        <Quiz
          id="q-cos"
          question="At an angle of 90° (straight up), what are cos and sin?"
          options={[
            { label: 'cos = 0, sin = 1', correct: true },
            { label: 'cos = 1, sin = 0', why: 'That’s at 0° — pointing right. At 90° you point straight up.' },
            { label: 'cos = 0.5, sin = 0.5', why: 'Straight up means no distance across, and the full distance (1) up.' },
          ]}
        />
        <NumberQuiz
          id="nq-rad"
          question="How many degrees is π radians?"
          answer={180}
          explain="π radians is half of a full turn (2π), so it’s 180°."
          hint={<>A full turn is 2π radians and also 360°.</>}
        />
        <Callout kind="notation">
          Because the point is always on a circle of radius 1, <M>{'\\cos^2\\theta + \\sin^2\\theta = 1'}</M> for every angle.
          The Greek letter <M>{'\\theta'}</M> (“theta”) is the usual name for an angle.
        </Callout>
      </Step>

      <Step>
        <H>In code</H>
        <Exercise id="trig-basics" title="Math.sin, Math.cos, Math.atan2" {...EX.trigBasics} />
      </Step>

      <Step gate="rotation">
        <H>Rotating points</H>
        <P>
          Sine and cosine let you <strong>rotate</strong> things. To turn a point around the origin, you mix its x and y using
          the cos and sin of the angle. Some language models use exactly this to encode word positions: they rotate each word’s
          numbers by an angle that depends on where the word sits in the sentence.
        </P>
        <Exercise
          id="rotation"
          title="toRadians, pointOnCircle, rotate"
          {...EX.rotation}
          hints={[
            <>
              <C>{'return [r * Math.cos(angle), r * Math.sin(angle)]'}</C>
            </>,
            <>
              <C>{'const [x, y] = p'}</C>, then apply the two formulas from the comment.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Waves</H>
        <P>
          Unrolled, sine is a <strong>wave</strong>, and three knobs control any wave: its height (<strong>amplitude</strong>{' '}
          A), how often it repeats (<strong>frequency</strong> f), and where it starts (<strong>phase</strong> φ). Add waves
          together and you can build almost any wiggle.
        </P>
        <Figure n="2.13" wide caption="A·sin(f·x + φ). Tick ‘add a second wave’ to see two waves add up to a new shape.">
          <WaveExplorer />
        </Figure>
      </Step>

      <Step gate="position-code">
        <H>A fingerprint made of waves</H>
        <P>
          Here’s a real use. The original Transformer gives every position in a sentence a <strong>fingerprint</strong> made
          of sine and cosine waves at many speeds. Fast waves tell nearby positions apart; slow waves tell far-apart ones apart.
          Together, no two positions look the same.
        </P>
        <Exercise
          id="position-code"
          title="encodePosition"
          {...EX.positionCode}
          hints={[
            <>
              Loop <C>{'for (let i = 0; i < dims / 2; i++)'}</C>, compute <C>{'const freq = 1 / 10000 ** ((2 * i) / dims)'}</C>.
            </>,
            <>
              Then <C>{'out.push(Math.sin(pos * freq), Math.cos(pos * freq))'}</C>.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-similar">
        <H>Angles measure similarity</H>
        <P>
          One more idea, and it’s one of the most important in modern AI. Give two arrows a direction each. The cosine of the
          angle between them is <strong>1</strong> when they point the same way, <strong>0</strong> when they’re at right
          angles, and <strong>−1</strong> when they point in opposite directions.
        </P>
        <Figure n="2.14" caption="Drag the arrow tips. The cosine of the angle between them is a ‘sameness’ score from −1 to 1, and the arrows’ lengths don’t affect it.">
          <AngleBetween />
        </Figure>
        <Quiz
          id="q-similar"
          question="Two arrows point in exactly the same direction, but one is three times as long. What is the cosine of the angle between them?"
          options={[
            { label: '1', correct: true },
            { label: '3', why: 'Length doesn’t matter, only direction. And cosine never goes above 1.' },
            { label: '1/3', why: 'The angle between them is 0°, and cos(0°) = 1, whatever their lengths.' },
          ]}
          explain="In Module 8, every word becomes an arrow in a space with hundreds of dimensions. Words with similar meanings point in similar directions, and their ‘cosine similarity’ is how we measure it."
        />
      </Step>
    </Lesson>
  )
}
